import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fetchResources, fetchSubjects, getResourceFileUrl } from '../services/api';
import { Download, Eye, PlayCircle, FileText } from 'lucide-react';
import PdfViewerModal from '../components/PdfViewerModal';
import PdfFirstPagePreview from '../components/PdfFirstPagePreview';

const syllabusMap = { local: 'Local', cambridge: 'Cambridge', edexcel: 'Edexcel' };

const ResourceListPage = () => {
  const { type, category } = useParams();
  const [resources, setResources] = useState([]);
  const [previewResource, setPreviewResource] = useState(null);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [filters, setFilters] = useState({
    subject: '',
    year: '',
    medium: '',
    session: '',
    variant: ''
  });

  useEffect(() => {
    fetchSubjects()
      .then((res) => {
        const syllabus = syllabusMap[type] || '';
        setSubjectOptions(res.data.filter((s) => s.syllabus === syllabus));
      })
      .catch(console.error);
  }, [type]);

  useEffect(() => {
    const loadResources = async () => {
      try {
        const res = await fetchResources(type, category, filters.subject);
        setResources(res.data);
      } catch (err) {
        console.error("Error loading resources:", err);
      }
    };
    loadResources();
  }, [type, category, filters.subject]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <h2 className="text-3xl font-bold mb-6 capitalize text-accentBlue">
        {type} - {category.replace('-', ' ')}
      </h2>

      {/* Dynamic Filter Bar */}
      <div className="flex flex-wrap gap-4 mb-10 p-6 bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl">
        <select
          value={filters.subject}
          onChange={(e) => setFilters({ ...filters, subject: e.target.value })}
          className="bg-darkBg border border-white/10 p-3 rounded-xl focus:outline-none focus:border-accentBlue"
        >
          <option value="">All Subjects</option>
          {subjectOptions.map((s) => (
            <option key={s._id} value={s.name}>{s.name}</option>
          ))}
        </select>
        
        <select 
          className="bg-darkBg border border-white/10 p-3 rounded-xl focus:outline-none"
          onChange={(e) => setFilters({...filters, year: e.target.value})}
        >
          <option value="">Year</option>
          {/* Loop years [cite: 68] */}
          {['2024', '2023', '2022'].map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        {/* Local Only Filters  */}
        {type === 'local' && (
          <select 
            className="bg-darkBg border border-white/10 p-3 rounded-xl focus:outline-none"
            onChange={(e) => setFilters({...filters, medium: e.target.value})}
          >
            <option value="">Medium</option>
            <option value="Sinhala">Sinhala</option>
            <option value="English">English</option>
            <option value="Tamil">Tamil</option>
          </select>
        )}

        {/* Cambridge/Edexcel Only Filters  */}
        {(type === 'cambridge' || type === 'edexcel') && (
          <>
            <select 
              className="bg-darkBg border border-white/10 p-3 rounded-xl focus:outline-none"
              onChange={(e) => setFilters({...filters, session: e.target.value})}
            >
              <option value="">Session</option>
              <option value="Feb/March">Feb/March</option>
              <option value="May/June">May/June</option>
              <option value="Oct/Nov">Oct/Nov</option>
            </select>
            <input 
              type="text" 
              placeholder="Variant (e.g. 11)"
              className="w-32 bg-darkBg border border-white/10 p-3 rounded-xl focus:outline-none"
              onChange={(e) => setFilters({...filters, variant: e.target.value})}
            />
          </>
        )}
      </div>

      {/* Resource Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {resources.map((item) => (
          <article key={item._id} className="group flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/5 transition-all hover:-translate-y-1 hover:border-accentBlue/30 hover:bg-white/[0.07] hover:shadow-xl hover:shadow-accentBlue/5">
            {item.fileUrl ? (
              <button
                type="button"
                onClick={() => setPreviewResource(item)}
                className="relative block w-full overflow-hidden border-b border-white/10 text-left"
                aria-label={`View ${item.title} PDF`}
              >
                <PdfFirstPagePreview
                  url={getResourceFileUrl(item._id)}
                  title={item.title}
                />
                <span className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/35">
                  <span className="flex translate-y-2 items-center gap-2 rounded-full bg-accentBlue px-4 py-2 text-xs font-bold text-black opacity-0 shadow-lg transition-all group-hover:translate-y-0 group-hover:opacity-100">
                    <Eye size={15} /> View PDF
                  </span>
                </span>
                {item.isPremium && (
                  <span className="absolute right-3 top-3 rounded-full bg-yellow-400 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider text-black shadow-lg">
                    Premium
                  </span>
                )}
              </button>
            ) : (
              <a
                href={item.videoUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="flex aspect-video items-center justify-center border-b border-white/10 bg-gradient-to-br from-accentBlue/15 to-purple-500/10 text-accentBlue"
              >
                <PlayCircle size={46} />
              </a>
            )}

            <div className="flex flex-1 flex-col p-5">
              <div className="mb-3 flex items-start gap-3">
                <span className="rounded-lg bg-accentBlue/10 p-2 text-accentBlue">
                  {item.fileUrl ? <FileText size={18} /> : <PlayCircle size={18} />}
                </span>
                <div className="min-w-0">
                  <h4 className="line-clamp-2 font-bold leading-snug text-white">{item.title}</h4>
                  <p className="mt-1 text-xs text-gray-400">
                    {[item.subject, item.year].filter(Boolean).join(' · ')}
                  </p>
                </div>
              </div>

              {item.fileUrl ? (
                <div className="mt-auto grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setPreviewResource(item)}
                    className="flex items-center justify-center gap-2 rounded-lg bg-accentBlue py-2.5 text-sm font-bold text-black transition hover:opacity-90"
                  >
                    <Eye size={16} /> View
                  </button>
                  <a
                    href={getResourceFileUrl(item._id, true)}
                    className="flex items-center justify-center gap-2 rounded-lg border border-accentBlue/40 bg-accentBlue/10 py-2.5 text-sm font-bold text-accentBlue transition hover:bg-accentBlue/20"
                  >
                    <Download size={16} /> Download
                  </a>
                </div>
              ) : (
                <a
                  href={item.videoUrl || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto flex w-full items-center justify-center rounded-lg bg-accentBlue py-2.5 font-bold text-black transition hover:opacity-90"
                >
                  <PlayCircle size={16} className="mr-2" /> View Video
                </a>
              )}
            </div>
          </article>
        ))}
      </div>

      <PdfViewerModal resource={previewResource} onClose={() => setPreviewResource(null)} />
    </div>
  );
};

export default ResourceListPage;
